import type { Transition, Variants } from 'framer-motion';

/**
 * Shared motion language for the navigator flow (and beyond): warm, quick,
 * sophisticated. The easing curve is the loading-page prototype's own
 * cubic-bezier(.2,.7,.2,1) so page motion and micro-interactions feel related.
 *
 * Reduced motion: wrap surfaces in <MotionConfig reducedMotion="user"> —
 * framer-motion then skips transform/layout animations for users who ask for
 * less motion. Infinite decorative loops should additionally gate on
 * useReducedMotion().
 */
export const warmEase = [0.2, 0.7, 0.2, 1] as const;

export const warmTransition: Transition = { duration: 0.38, ease: warmEase };

export const fadeInUp: Variants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: warmTransition },
  exit: { opacity: 0, y: -8, transition: { duration: 0.22, ease: warmEase } },
};

export const fadeIn: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: warmTransition },
  exit: { opacity: 0, transition: { duration: 0.18, ease: warmEase } },
};

/** Slightly richer entrance for celebratory moments (CTA reveal, success). */
export const popIn: Variants = {
  initial: { opacity: 0, scale: 0.96 },
  animate: { opacity: 1, scale: 1, transition: { duration: 0.26, ease: warmEase } },
  exit: { opacity: 0, scale: 0.98, transition: { duration: 0.18, ease: warmEase } },
};

/** Parent container that staggers `fadeInUp`/`fadeIn` children in. */
export const staggerContainer: Variants = {
  initial: {},
  animate: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

/** Route-level enter/exit for page changes. */
export const pageVariants: Variants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.32, ease: warmEase } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.18, ease: warmEase } },
};
